import React, { useState } from 'react';
import styled from 'styled-components';

import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import { tokens } from 'containers/Intelligence/theme';
import { CardMetric, MetricTone } from 'containers/Intelligence/types';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import MetricValue from '../MetricValue';
import PeriodDelta from '../PeriodDelta';
import TileSparkline from '../TileSparkline';
import { formatMetricReading } from '../formatMetric';

export interface IStatBoxProps
  extends Pick<
    CardMetric,
    | 'label'
    | 'value'
    | 'format'
    | 'denominator'
    | 'delta'
    | 'sparkline'
    | 'valueText'
    | 'valueSuffix'
    | 'status'
    | 'notConfigured'
    | 'dataState'
  > {
  /** Symbol or ISO code, meaningful only for `currency` metrics. Threaded down by the page container. */
  currency?: string;
  /**
   * How the tile draws `sparkline`. `bars` is the seven-bar trend `589-29844` gives the growth
   * tiles; `line` is the marked line chart of `589-29285`, which `2558:3744` puts on all four Telco
   * Churn tiles. A presentation choice of the CARD, not of the payload — the same series can be
   * read either way, and the screens do — so it arrives as a prop rather than as a metric field.
   */
  trendVariant?: 'bars' | 'line';
  /**
   * A glyph the tile draws where a trend would go — Customer 360's `Last Engagement` (`3583-9769`)
   * puts a 32px calendar in that slot and no chart. Decorative: the tile is already named by its own
   * label, so the caller passes it `aria-hidden`. PLA-8828.
   */
  icon?: React.ReactNode;
  /**
   * Hue for a `valueText` reading whose own word carries the valence — `Medium` on Customer 360's
   * `Highest Severity (30D)` tile is drawn in --is-warning, not in the neutral ink every other
   * categorical reading takes (`3583-9243`). PLA-8828.
   */
  valueTone?: MetricTone;
  /**
   * A line of the caller's own under the reading row and inside the tile. Opt-in, because the two
   * tiles that need one draw different things there — a green `Low Risk` band under the FAS
   * reading and a plain caption under `Latest Event` (`3583-9195`, `3583-9236`) — and `status`
   * rides the figure's baseline rather than taking a row. PLA-8828.
   */
  footNote?: React.ReactNode;
  onViewDetails?: () => void;
  className?: string;
}

/* The tile trend, measured at 100% on `589-29844` and again on `589-29936` — the two growth cards
   draw the SAME chart. Seven bars on a shared baseline, 8px wide at an 11.5px pitch (the runs read
   12/12/11/12/12/11, which is a 3.5px gap rather than an alternating one), the tallest 37px: the
   plot spans x 877..954 and y 567..603 inside a 308 x 78 tile. The chart had been drawn 33 x 24 out
   of 3px bars, under half the width the file gives it. */
const SPARKLINE_HEIGHT = 37;
const SPARKLINE_MIN_BAR_HEIGHT = 2;
const SPARKLINE_BAR_WIDTH = 8;
const SPARKLINE_BAR_GAP = 3.5;

const StatBoxWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${tokens.space['12']} ${tokens.space['16']};
  border: 1px solid ${tokens.color['border-subtle']};
  border-radius: ${tokens.radius.md};
  background: ${tokens.color['surface-card-alt']};

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * + * {
    margin-top: ${tokens.space['8']};
  }
`;

const Label = styled.span`
  color: ${tokens.color['text-secondary']};
  font-size: ${tokens.type['metric-title-size']};
  font-weight: ${tokens.type['metric-title-weight']};
  line-height: ${tokens.type['metric-title-line']};
`;

// A categorical or not-yet-configured reading states itself in words, so it takes the figure's
// slot rather than MetricValue's "no reading" placeholder - the screens draw `Not Configured`
// there, which is a designed state and not a missing number.
const StatusWord = styled.span<{ $tone?: MetricTone }>`
  color: ${({ $tone }) => ($tone ? TONE_INKS[$tone] : tokens.color['text-primary'])};
  font-size: ${tokens.type['stat-size']};
  font-weight: ${tokens.type['stat-weight']};
  line-height: ${tokens.type['stat-line']};
`;

// Rides the reading's baseline at the label step, the way the band word does — it is a smaller part
// of the same reading, not a second one. Amber is what 3583-9236 draws it in. PLA-8828.
const ValueSuffix = styled.span`
  color: ${tokens.color.warning};
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['label-weight']};
  line-height: ${tokens.type['label-line']};
`;

// The band word some tiles print where a movement would go — `High` beside `78` on 589:32777. It
// takes the delta's slot and type, not the denominator's: the denominator is a second LINE under the
// figure, and this sits on the figure's own baseline.
const TONE_INKS: Record<MetricTone, string> = {
  positive: tokens.color.positive,
  warning: tokens.color.warning,
  negative: tokens.color.negative,
  info: tokens.color['accent-blue'],
  accent: tokens.color['accent-purple']
};

const BandWord = styled.span<{ $tone: MetricTone }>`
  color: ${({ $tone }) => TONE_INKS[$tone]};
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['label-weight']};
  line-height: ${tokens.type['label-line']};
`;

const ValueRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;

  > * + * {
    margin-left: ${tokens.space['12']};
  }
`;

// The movement rides the figure's own baseline, to its right, rather than taking a row underneath:
// on `589:27759`/`589:27760` the value box ends at x 97.5 and the delta box starts at x 97.21, and on
// `589:32340` the delta is a span INSIDE the value's text node. `wrap` is what keeps a wide figure
// plus a wide movement inside a 1280 tile. PLA-8815.
const Reading = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  min-width: 0;

  > * + * {
    margin-left: ${tokens.space['8']};
  }
`;

/* Focusable, because the reading it reveals is available on hover and a keyboard reader has no
   hover. `role="img"` carrying the series' own name, and the active reading announced from the
   readout beside it rather than from inside the image, whose contents assistive tech ignores.
   Follows `RiskProtectionSection`'s tile trend; recharts is pinned at 2.5.0 here, whose `Tooltip`
   is pointer-only (`accessibilityLayer` arrived in 2.10), so a hover reveal has to be hand-rolled. */
const Sparkline = styled.div`
  display: flex;
  align-items: flex-end;
  height: ${SPARKLINE_HEIGHT}px;
  border-radius: ${tokens.radius.xs};
  cursor: crosshair;

  > * + * {
    margin-left: ${SPARKLINE_BAR_GAP}px;
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${tokens.elevation['focus-ring']};
  }
`;

// Two-tone by design, not by state alone: the file draws six bars in --is-trend-bar and the seventh
// in --is-positive, and the highlight is what reproduces that seventh. It rests on the peak — the
// state the screens are captured in — and follows the pointer from there, because a still of a hover
// and a permanent mark are indistinguishable in a capture and only one of them still means something
// when the payload changes.
const SparklineBar = styled.span<{ $active: boolean }>`
  width: ${SPARKLINE_BAR_WIDTH}px;
  border-radius: ${tokens.radius.pill};
  background: ${({ $active }) => ($active ? tokens.color.positive : tokens.color['trend-bar'])};
`;

// An unmeasured reading holds its slot open but draws nothing, so the bars either side stay on their
// own points in the trend instead of closing up over the missing one.
const SparklineGap = styled.span`
  width: ${SPARKLINE_BAR_WIDTH}px;
`;

/* The foot of the tile. `View Details` is right-aligned under the chart — on `589-29844` its box
   ends at x 957 against a tile right edge of 973, the same 16px inset the chart's last bar keeps —
   and it had been drawn at the tile's LEFT edge. The revealed reading takes the free half of that
   row, where it cannot cover the headline figure above it, and the row is present whenever either
   part is, so revealing a reading moves nothing. */
const FootRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;

  > * + * {
    margin-left: ${tokens.space['8']};
  }
`;

const SparkReadout = styled.span`
  min-width: 0;
  overflow: hidden;
  color: ${tokens.color['text-secondary']};
  font-size: ${tokens.type['card-description-size']};
  line-height: ${tokens.type['card-description-line']};
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ViewDetailsButton = styled.button`
  flex: 0 0 auto;
  margin-left: auto;
  padding: 0;
  border: none;
  background: none;
  color: ${tokens.color['accent-blue']};
  cursor: pointer;
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['label-weight']};
  line-height: ${tokens.type['label-line']};

  &:focus-visible {
    outline: none;
    box-shadow: ${tokens.elevation['focus-ring']};
  }
`;

// `null` marks a reading the payload never carried. Drawing it at the floor height would be
// indistinguishable from a measured zero, so it is dropped from the plot the way TrendChart drops a
// non-finite point — and kept out of the peak, which a NaN would otherwise poison for every bar.
const barHeightsFor = (values: number[]): (number | null)[] => {
  const readings = values.map(value => (Number.isFinite(value) ? Math.max(value, 0) : null));
  const peak = Math.max(...readings.map(reading => reading ?? 0), 0);

  return readings.map(reading => {
    if (reading === null) {
      return null;
    }

    if (peak <= 0) {
      return SPARKLINE_MIN_BAR_HEIGHT;
    }

    const scaled = Math.round((reading / peak) * SPARKLINE_HEIGHT);

    return Math.max(scaled, SPARKLINE_MIN_BAR_HEIGHT);
  });
};

const StatBox: React.FC<IStatBoxProps> = ({
  label,
  value,
  format,
  currency,
  denominator,
  delta,
  sparkline,
  valueText,
  valueSuffix,
  status,
  notConfigured,
  dataState,
  trendVariant = 'bars',
  icon,
  valueTone,
  footNote,
  onViewDetails,
  className
}) => {
  const { t } = useMultiLanguageTranslation();
  // `null` is "resting", which is NOT the same as "no bar highlighted": at rest the highlight sits on
  // the peak, reproducing the state the screens are captured in, and the readout stays empty.
  const [active, setActive] = useState<number | null>(null);

  // A tile with no reading shows `MetricValue`'s placeholder and drops its movement row and trend:
  // `↑ 940` beside a missing value reads as a change from nothing, and the honest reading of the
  // whole tile is the card-level state `InsightCard` renders (FR § Data states).
  const hasValue = value !== null && value !== undefined;
  const readings = hasValue && sparkline ? sparkline : [];
  const barHeights =
    readings.length > 0 && trendVariant === 'bars' ? barHeightsFor(readings) : null;
  const hasTrend = Boolean(barHeights) && barHeights.some(height => height !== null);
  // The line variant carries its own hover marker and its own live readout, so it takes the trend
  // slot whole: no bar plot, and no `FootRow` readout that would announce the same point twice.
  const lineTrend = readings.length > 0 && trendVariant === 'line';
  // The indices the payload actually measured. Everything below steps through THESE, so an
  // unmeasured slot can neither be highlighted nor win the peak.
  const measured = barHeights
    ? barHeights.reduce<number[]>(
        (keep, height, index) => (height === null ? keep : keep.concat(index)),
        []
      )
    : [];
  const peak = measured.reduce(
    (highest, index) => (readings[index] > readings[highest] ? index : highest),
    measured[0]
  );
  const highlighted = active === null ? peak : active;

  // Nearest measured bar to the pointer, taken over the box's OWN width so the highlight tracks the
  // cursor at whatever width the tile is laid out at, and over `length - 1` so a longer series from
  // the live seam simply narrows the pitch instead of running off the end.
  const nearestTo = (clientX: number, box: DOMRect): number => {
    const pitch = barHeights.length > 1 ? box.width / (barHeights.length - 1) : 0;
    const at = pitch > 0 ? (clientX - box.left) / pitch : 0;

    return measured.reduce(
      (closest, index) => (Math.abs(index - at) < Math.abs(closest - at) ? index : closest),
      measured[0]
    );
  };

  const step = (delta: number): number => {
    const from = measured.indexOf(highlighted);
    const next = Math.min(Math.max(from + delta, 0), measured.length - 1);

    return measured[next];
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    const moves: Record<string, () => number> = {
      ArrowRight: () => step(1),
      ArrowLeft: () => step(-1),
      Home: () => measured[0],
      End: () => measured[measured.length - 1]
    };

    if (moves[event.key]) {
      event.preventDefault();
      setActive(moves[event.key]());
    }
  };

  return (
    <StatBoxWrapper className={className} data-testid="stat-box">
      <Label data-testid="stat-box-label">{label}</Label>

      <ValueRow>
        <Reading>
          {valueText === undefined ? (
            <MetricValue
              value={value}
              format={format}
              currency={currency}
              denominator={denominator}
              notConfigured={notConfigured}
              dataState={dataState}
            />
          ) : (
            <StatusWord $tone={valueTone} data-testid="stat-box-status-word">
              {valueText}
            </StatusWord>
          )}

          {valueSuffix ? (
            <ValueSuffix data-testid="stat-box-value-suffix">{valueSuffix}</ValueSuffix>
          ) : null}

          {hasValue && delta ? <PeriodDelta {...delta} currency={currency} /> : null}
          {hasValue && !delta && status ? (
            <BandWord $tone={status.tone} data-testid="stat-box-band-word">
              {status.label}
            </BandWord>
          ) : null}
        </Reading>

        {lineTrend ? (
          <TileSparkline
            points={readings}
            // The trend reads the tile's own valence rather than a hue of its own, the rule
            // `RiskProtectionSection` already applies to the same widget: a band word wins, and
            // without one the movement's sentiment decides.
            color={
              TONE_INKS[status?.tone ?? (delta?.sentiment === 'positive' ? 'positive' : 'negative')]
            }
            label={label}
            formatReading={reading => formatMetricReading(reading, format, currency)}
          />
        ) : null}

        {hasTrend ? (
          <Sparkline
            role="img"
            tabIndex={0}
            aria-label={t(`${TCustomerInsights}.Chart.TrendSeries`, { series: label })}
            data-testid="stat-box-sparkline"
            data-points={measured.length}
            onMouseMove={event =>
              setActive(nearestTo(event.clientX, event.currentTarget.getBoundingClientRect()))
            }
            onMouseLeave={() => setActive(null)}
            onFocus={() => setActive(current => (current === null ? peak : current))}
            onBlur={() => setActive(null)}
            onKeyDown={onKeyDown}
          >
            {barHeights.map((height, index) =>
              height === null ? (
                <SparklineGap key={index} data-testid="stat-box-sparkline-gap" />
              ) : (
                <SparklineBar
                  key={index}
                  $active={index === highlighted}
                  style={{ height: `${height}px` }}
                  data-active={index === highlighted}
                  data-testid="stat-box-sparkline-bar"
                />
              )
            )}
          </Sparkline>
        ) : null}

        {icon}
      </ValueRow>

      {footNote ? <div data-testid="stat-box-foot-note">{footNote}</div> : null}

      {hasTrend || onViewDetails ? (
        <FootRow data-testid="stat-box-foot">
          {hasTrend ? (
            <SparkReadout aria-live="polite" data-testid="stat-box-sparkline-reading">
              {active === null
                ? ''
                : t(`${TCustomerInsights}.Chart.PointReading`, {
                    label,
                    value: formatMetricReading(readings[active], format, currency)
                  })}
            </SparkReadout>
          ) : null}

          {onViewDetails ? (
            <ViewDetailsButton
              type="button"
              onClick={onViewDetails}
              aria-label={t(`${TCustomerInsights}.Actions.ViewDetailsFor`, { label })}
              data-testid="stat-box-view-details"
            >
              {t(`${TCustomerInsights}.Actions.ViewDetails`)}
            </ViewDetailsButton>
          ) : null}
        </FootRow>
      ) : null}
    </StatBoxWrapper>
  );
};

export default StatBox;
